import { persistentAtom } from '@nanostores/persistent';
import { map, computed } from 'nanostores';
import { toast } from 'react-toastify';
import type { SurveyData } from '~/components/dialogs/survey';
import { clientFetch } from '~/lib/.client/fetch';
import { userStore } from '~/lib/stores/user';
import type { SurveyStatus } from '~/types/user';

interface UpdateSurveyRequest {
  data?: Record<string, any>;
  finish?: boolean;
  dismiss?: boolean;
  seen?: boolean;
}

interface SurveyState {
  data: SurveyData;
  status: SurveyStatus;
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const LOCAL_STORAGE_DISMISS_KEY = 'surveyLastDismissed';

class SurveyStore {
  $surveyData = map<SurveyState>({ data: {}, status: 'finished' });

  $surveyLastDismissedTimestamp = persistentAtom<number | null>(LOCAL_STORAGE_DISMISS_KEY, null, {
    encode: (value) => (value === null ? '' : String(value)),
    decode: (value) => (value === '' || value === undefined || value === null ? null : Number(value)),
  });

  $surveyCompleted = computed([userStore.user, this.$surveyData], (user, data) => {
    return user?.surveys?.onboarding === 'finished' || data.status === 'finished';
  });

  readonly canSkipSurvey = computed(userStore.user, (user) => {
    if (!user) {
      return false;
    }

    const now = new Date();
    const accountCreatedAt = new Date(user.createdAt);
    const accountAge = now.getTime() - accountCreatedAt.getTime();

    return accountAge > TWENTY_FOUR_HOURS_MS;
  });

  readonly showSurvey = computed(
    [userStore.loggedIn, this.$surveyCompleted, this.$surveyLastDismissedTimestamp],
    (loggedIn, surveyCompleted, lastDismissed) => {
      if (import.meta.env.VITE_DISABLE_AUTH) {
        return false;
      }

      if (!loggedIn || surveyCompleted || surveyCompleted === null) {
        return false;
      }

      if (lastDismissed === null) {
        return true;
      }

      const twentyFourHoursAgo = Date.now() - TWENTY_FOUR_HOURS_MS;

      return lastDismissed < twentyFourHoursAgo;
    },
  );

  async load() {
    if (import.meta.env.SSR) {
      return;
    }

    // we shouldn't even render the survey if this is true, but let's check it anyways
    const isCompleted = userStore.getUser()?.surveys?.onboarding === 'finished';

    if (isCompleted) {
      return;
    }

    const response = await clientFetch('/api/survey', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    try {
      if (!response.ok) {
        throw new Error('Failed to load survey');
      }

      const survey = await response.json<{ onboarding: SurveyState }>();

      this.$surveyData.set(survey.onboarding);
    } catch {
      // do nothing if we couldn't load the survey data
    }
  }

  async dismissSurvey() {
    this.$surveyLastDismissedTimestamp.set(Date.now());

    try {
      await this.#updateSurvey({ dismiss: true });
    } catch {
      // do nothing if we couldn't mark it as dismissed
    }
  }

  completeSurvey() {
    this.$surveyLastDismissedTimestamp.set(null);

    this.$surveyData.setKey('status', 'finished');
  }

  async updateSurveyData(data: Record<string, any>, isFinished?: boolean) {
    if (import.meta.env.SSR) {
      return;
    }

    if (!data || !Object.keys(data).length) {
      return;
    }

    const payload: UpdateSurveyRequest = { data };

    if (isFinished) {
      payload.finish = true;
    }

    try {
      await this.#updateSurvey(payload);
    } catch {
      // TODO not sure if this is the right way to handle this
      if (isFinished) {
        toast.error('Failed to store your survey results');

        this.$surveyLastDismissedTimestamp.set(Date.now());
      }
    }
  }

  async #updateSurvey(body: UpdateSurveyRequest) {
    const response = await clientFetch('/api/survey', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error('Failed to update survey');
    }
  }
}

export const surveyStore = new SurveyStore();
